import type { Messages } from './de';

// British English. Glossary (keep consistent): Haushaltstracker → Budget
// tracker, Einzelplan → budget section, Kapitel → chapter, Titel → budget
// title, Soll → planned, Ist → actual, Sondervermögen → special fund,
// Hauptgruppe/Obergruppe/Gruppierung → main group/group/subgroup,
// Hauptfunktion/Oberfunktion/Funktion → main function/function/subfunction,
// Bereichsausnahme → defence carve-out. Source: the reviewed workbook
// Haushaltstracker_Übersetzungen_corrected.xlsx.
export const en: Messages = {
  common: {
    close: 'Close',
    back: 'Back',
    done: 'Done',
    reset: 'Reset',
    search: 'Search...',
    removeFilter: 'Remove filter',
    noData: 'No data',
    ist: 'Actual',
    soll: 'Planned',
    year: (year: number) => `Year: ${year}`,
    page: (page: number | string | null) => `p. ${page ?? ''}`,
    source: 'Source: Federal budget, dezernatzukunft.org/daten/haushaltstracker',
  },

  units: {
    absolute: '€ bn',
    gdpPercent: '% of GDP',
    budgetPercent: '% of budget',
  },

  widget: {
    loading: 'Loading budget data...',
    loadError: (error: string) => `Error loading data: ${error}`,
    errorBanner: (parts: string) =>
      `Some data could not be loaded: ${parts}. The display is limited.`,
    errorPartGdp: 'GDP data',
    errorPartTotals: 'budget totals',
    errorPartFilterOptions: 'filter options',
    retry: 'Try again',
    dismissNotice: 'Dismiss notice',
    toastGdpUnavailable: 'GDP data unavailable — switching to absolute values',
    toastTotalsUnavailable: 'Budget totals unavailable — switching to absolute values',
    typeToggleLabel: 'Revenue or expenditure',
    revenue: 'Revenue',
    expenditure: 'Expenditure',
    unit: 'Unit',
    rootRevenue: 'Total federal revenue',
    rootExpenditure: 'Total federal expenditure',
    firstPublishedDate: '12 June 2026',
    footerDates: (lastUpdated: string) =>
      `First published: 12 June 2026 | Last updated: ${lastUpdated}`,
    methodology:
      'The overview and the dataset of federal expenditure at budget-title level are based on an automated capture of the actual (outturn) values of all expenditure titles since 2016, taken from the federal budget accounts (Haushaltsrechnungen) for the core budget and, where available, the special funds. For years without actuals yet, planned values are used; where supplementary budgets were passed, the most recent budgetary figures are used. To capture only expenditure that represents actual flows of money into the economy, the data are adjusted for transfers to reserves and for financial flows between the federal budget and the special funds. Expenditure is allocated to subsidies or public services according to the recipient: payments that go directly to households and businesses count as subsidies. Payments to public institutions, or payments through which the federal government itself provides a service, count as public services. Where expenditure benefits several groups of recipients, it is allocated to the largest group. The dataset also shows expenditure in selected policy areas separately (e.g. the Climate and Transformation Fund, KTF). Because of inconsistencies in the layout of the printed federal budgets, the automated capture may miss budget titles, which then have to be corrected by hand. Differences in methodology may also lead to small deviations from the total expenditure reported (and adjusted) by the federal government. These amount to no more than 0.8 per cent of annual expenditure. We are continuously working to improve data quality. Users may freely use, transform and distribute these data, provided the source is correctly cited. Positions taken on the basis of the data are those of the users, not those of Dezernat Zukunft.',
    contact: 'Contact:',
  },

  chart: {
    noTitleData: 'No budget title data available',
    noDataForFilters: 'No data for the current filters',
    noDataForYear: (year: number) => `No data available for ${year}`,
    hintSeparator: ' – ',
    hintDrill: 'Click for breakdown',
    hintFilterTable: 'click to filter the table',
    hintTitlesPick: 'click a title to filter, Other for more titles',
    areaSingleTitle: 'Planned and actual values compared',
    areaRoot: 'Actual values (shaded: planned values for years without actuals)',
    topTitles: 'Top 5 budget titles + Other',
    subTypes: 'Subcategories',
    barSingleTitle: 'Single budget title selected',
    barRootIst: 'Actual values',
    barRootSoll: 'Planned values (no actuals yet)',
    barRootRevenue: (year: number) => `Total federal revenue ${year} - annual view`,
    barRootExpenditure: (year: number) => `Total federal expenditure ${year} - annual view`,
    sollBand: 'Planned values',
    showSonstigesTitles: 'Show titles in Other',
    backToSonstiges: 'Move back to Other',
    backToSonstigesAria: (name: string) => `Move ${name} back to Other`,
    tooltipIst: 'Actual value',
    tooltipSoll: 'Planned value (no actuals yet)',
    expand: 'Expand chart',
    sollYearsCaption: '* Years without actuals show planned values',
    exportChart: 'Export chart',
    downloadPng: 'Download as PNG',
    downloadSvg: 'Download as SVG',
    embedChart: 'Embed chart',
  },

  tooltip: {
    yearWithType: (year: number, isIst: boolean) => `Year: ${year} (${isIst ? 'actual' : 'planned'})`,
    total: (value: string) => `Total: ${value}`,
    more: (count: number) => `... and ${count} more`,
    sollLabel: 'Planned:',
    istLabel: 'Actual:',
    einzelplanShort: 'Budget section',
    kapitelShort: 'Ch.',
  },

  titleDetails: {
    fallbackTitle: 'Budget title details',
    einzelplan: 'Budget Section (Einzelplan):',
    kapitel: 'Chapter:',
    history: 'Historical data',
    year: 'Year',
  },

  sonstigesPicker: {
    dialogLabel: 'Choose budget titles from Other',
    heading: 'Included in “Other”',
    loading: 'Loading…',
    count: (count: number) => `${count} ${count === 1 ? 'budget title' : 'budget titles'} · largest share first`,
    noMoreTitles: 'No further budget titles',
    searchPlaceholder: 'Search budget titles…',
    searchAria: 'Search budget titles',
    loadError: 'Budget titles could not be loaded.',
    noMatch: 'No budget title found.',
    noMoreInSonstiges: 'No further budget titles in Other.',
    colTitle: 'Budget title',
    colIst: 'Highest actual',
    colShare: 'Share of Other',
    rowRemove: 'Move back to Other',
    rowAdd: 'Show as its own area',
    selected: (count: number) => `${count} selected`,
    prompt: 'Choose budget titles to drill down',
  },

  filterPanel: {
    title: 'Filters',
    reset: 'reset',
    closeSheet: 'Close filters',
    politikfeld: 'Policy areas & recipients',
    oberkategorie: 'Main category',
    allOberkategorien: 'All main categories',
    unterkategorie: 'Subcategory',
    allUnterkategorien: 'All subcategories',
    haushaltsstelle: 'Budget position',
    einzelplan: 'Budget section',
    allEinzelplaene: 'All budget sections',
    kapitel: 'Chapter',
    allKapitel: 'All chapters',
    sondervermoegen: 'Special fund',
    allSondervermoegen: 'All special funds',
    gruppierungSection: 'Economic classification',
    hauptgruppe: 'Main group',
    allHauptgruppen: 'All main groups',
    obergruppe: 'Group',
    allObergruppen: 'All groups',
    gruppierung: 'Subgroup',
    allGruppierungen: 'All subgroups',
    funktionSection: 'Functional classification',
    hauptfunktion: 'Main function',
    allHauptfunktionen: 'All main functions',
    oberfunktion: 'Function',
    allOberfunktionen: 'All functions',
    funktion: 'Subfunction',
    allFunktionen: 'All subfunctions',
  },

  select: {
    noResultsFor: (term: string) => `No results for "${term}"`,
    noResults: 'No results',
    resultsCount: (shown: number, total: number) => `${shown} of ${total} results`,
    selectedCount: (count: number) => `${count} selected`,
  },

  search: {
    placeholder: 'Search budget titles...',
    einzelplan: (code: string) => `Sec. ${code}`,
    kapitel: (code: string) => `Ch. ${code}`,
  },

  chips: {
    keys: {
      query: 'Search',
      expenditureType: 'Main category',
      subExpType: 'Subcategory',
      einzelplan: 'Budget section',
      kapitel: 'Chapter',
      hauptgruppe: 'Main group',
      obergruppe: 'Group',
      gruppierung: 'Subgroup',
      hauptfunktion: 'Main function',
      oberfunktion: 'Function',
      funktion: 'Subfunction',
      prefix: 'Prefix',
      kapitelScope: 'Scope',
      drillDownType: 'Main category',
      drillDownSubType: 'Subcategory',
      selectedTitle: 'Budget title',
    },
    query: (value: string) => `“${value}”`,
    resetAllAria: 'Reset all filters',
    resetAll: '↺ Reset all',
    caption: 'Active filters:',
    removeAria: (key: string) => `Remove filter ${key}`,
  },

  filterInfo: {
    prefix: (parts: string) => `Filtered by: ${parts}`,
    einzelplan: 'Budget section',
    kapitel: 'Chapter',
    funktion: 'Subfunction',
    oberfunktion: 'Function',
    hauptfunktion: 'Main function',
    gruppierung: 'Subgroup',
    obergruppe: 'Group',
    hauptgruppe: 'Main group',
    prefixLabels: { B: 'Defence carve-out' } as Record<string, string>,
    titlePrefix: (prefix: string) => `Budget title prefix ${prefix}`,
    kernhaushalt: 'Core budget (excluding special funds)',
    query: (query: string) => `Search: "${query}"`,
  },

  table: {
    root: 'Budget titles',
    entries: (count: string) => `${count} entries`,
    germanTitlesHint: 'Budget titles are shown in their original German wording.',
    exportCsv: 'Export as CSV',
    noEntries: 'No entries found',
    colTitel: 'Title',
    colZweckbestimmung: 'Purpose',
    colPolitikfeld: 'Policy area & recipient',
    cardPolitikfeld: 'Policy area',
    colIst: (year: number) => `Actual (${year})`,
    colSoll: (year: number) => `Planned (${year})`,
    details: 'Show details',
  },

  pagination: {
    previousAria: 'Previous page',
    previous: 'Back',
    nextAria: 'Next page',
    next: 'Next',
  },

  exportDialog: {
    title: 'Export data',
    loading: 'Loading data...',
    building: 'Building CSV...',
    failed: 'Export failed',
    current: 'Current view',
    currentDescription: (count: string) => `${count} entries with the current filters`,
    all: 'All data',
    allDescription: 'Export all entries without filters',
    cancel: 'Cancel',
    export: 'Export',
  },

  embedDialog: {
    title: 'Embed chart',
    intro:
      'This code shows the chart exactly as currently displayed — with the same filters and the same breakdown. The data stay live.',
    height: 'Height (px)',
    pinYear: (year: number) => `Pin year ${year}`,
    unpinnedHint: 'Without a pinned year, the embed always shows the latest budget year.',
    resizeHint: (height: number) =>
      `The second line is optional — it adjusts the height to the content automatically. Without it, the embed stays at ${height} px.`,
    copied: 'Copied to clipboard.',
    copy: 'Copy code',
  },

  embed: {
    trackerLink: 'Go to the full budget tracker →',
    unavailableTitle: 'This view is no longer available.',
    unavailableBody:
      'There are no data for the embedded selection — the budget may have changed since the chart was embedded.',
  },

  spotlights: {
    title: 'Spotlights',
    cards: {
      kernhaushalt: {
        label: 'Core budget',
        title: 'Core budget',
        description: 'Excluding special funds.',
      },
      'klima-fonds': {
        label: 'KTF',
        title: 'Climate and Transformation Fund',
        description: 'What the federal government spends on climate action.',
      },
      svik: {
        label: 'SVIK',
        title: 'Special Fund for Infrastructure and Climate Neutrality',
        description: '€500 billion for infrastructure and climate action at federal and state level.',
      },
      bereichsausnahmen: {
        label: 'Defence carve-out',
        title: 'Defence carve-out',
        description: 'Defence and security spending that is exempt from the debt brake.',
      },
      investitionen: {
        label: 'Investment',
        title: 'Investment',
        description: 'Rail, roads, housing and more.',
      },
      'subventionen-unternehmen': {
        label: 'Subsidies to businesses',
        title: 'Subsidies to businesses',
        description: 'How much money businesses receive from the federal government.',
      },
      'oeffentliche-leistungen': {
        label: 'Public services',
        title: 'Public services',
        description: 'Payments to public institutions, or services the federal government provides itself.',
      },
      finanzierungskosten: {
        label: 'Financing costs',
        title: 'Financing costs',
        description: 'What the federal government pays on its debt.',
      },
      bildung: {
        label: 'Education',
        title: 'Education',
        description: 'What the federal government spends on education.',
      },
    },
  },

  csv: {
    einzelplan: 'Budget section',
    kapitel: 'Chapter',
    type: 'Revenue/Expenditure',
    titelFunktion: 'Title code',
    zweckbestimmung: 'Purpose',
    kategorie: 'Category',
    oberkategorie: 'Main category',
    unterkategorie: 'Subcategory',
    ist: 'Actual',
    soll: 'Planned',
    positionChange: 'position_change',
    exportFilenamePrefix: 'budget_export',
    chartFilenamePrefix: 'budget_chart',
  },
};
